///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Re-open a cloud dataset on this device: download the stored file and
/// run it through the same handler pipeline as a local drop, producing a
/// dataset browser window and globe layer.
///

import { CSVFileHandler, GeoJSONFileHandler } from "@/components/features/file-drop/handlers"
import type { AppDispatch, RootState } from "@/redux/store"
import { mountImageOverlay } from "@/utils/imageOverlayMount"
import { datasetUrl } from "@/utils/datasetUrl"
import type { UserDatasetRecord } from "@shared-types"

export async function openCloudDataset(
	dataset: UserDatasetRecord,
	dispatch: AppDispatch,
	getState: () => RootState
): Promise<void> {
	const response = await fetch(datasetUrl(dataset.cdn_url))
	if (!response.ok) {
		throw new Error(`Download failed (${response.status})`)
	}
	const blob = await response.blob()
	const file = new File([blob], dataset.filename, {
		type: dataset.format === "geojson"
			? "application/geo+json"
			: dataset.format === "image" ? (blob.type || "image/png") : "text/csv",
	})

	const cloudDataset = {
		id: dataset.id,
		name: dataset.name,
		filename: dataset.filename,
		format: dataset.format,
		cdnUrl: dataset.cdn_url,
		featureCount: dataset.feature_count ?? undefined,
	}

	// Image ground overlays skip the tabular pipeline: decode and drape
	// at the stored georeference
	if (dataset.format === "image") {
		await mountImageOverlay(dispatch, getState, file, {
			bounds: dataset.bounds ?? undefined,
			cloudDataset,
		})
		return
	}

	const context = { dispatch, getState }
	const handler = dataset.format === "geojson"
		? new GeoJSONFileHandler(context)
		: new CSVFileHandler(context)
	handler.initialize()
	try {
		await handler.handleFile(file)
	} finally {
		handler.cleanup()
	}
}
